import {useCallback} from 'react';

import {
  envIsIntegration,
  envIsLocal,
  envIsProd,
  envIsPublicCloud,
} from '../config';
import {
  useUpdateUserInfoMutation,
  useViewerUserInfoQuery,
} from '../generated/graphql';
import {Analytics} from '../services/analytics';
import {useViewer} from '../state/viewer/hooks';
import {Viewer} from '../state/viewer/types';
import {UserInfo} from '../types/graphql';
import {BetaFeature, BetaFeatureKey} from './betaFeatures';
import {useFeatureFlagPanelSearchBeta} from './useFeatureFlag';

export function useBetaFeatureUnicornModeEnabled(): boolean {
  return useBetaFeature('unicorn-plot').isEnabled;
}

export const useBetaFeatureNamedWorkspaces = () => {
  return useBetaFeature('named-workspaces').isEnabled;
};

export function useBetaFeatureModelRegistryEnabled(): boolean {
  return useBetaFeature('model-registry').isEnabled;
}

export function useBetaFeatureNightModeEnabled(): boolean {
  return useBetaFeature('night').isEnabled;
}

export function useBetaFeatureVega3Enabled(): boolean {
  return useBetaFeature('vega3').isEnabled;
}

export function useBetaFeatureWeave1(): boolean | undefined {
  const ff = useBetaFeature('weave1');
  const isWandbEmployee = useViewerIsWandbEmployee();

  // TODO: some non-employees have weave1 explicitly set to false,
  // invalidating the logic below. This is a temporary fix to prevent
  // those users from being unable to force-bucket into weave1. We should
  // remove this once we have a better solution.
  if (!isWandbEmployee && ff.userSelectedValue === false) {
    return undefined;
  }

  // Here we use the userSelectedValue instead of isEnabled because we want to
  // return undefined if the user has not selected a value yet.
  // This is because we want to let experiment bucketing decide whether a user
  // should get weave1 if they have not explicitly opted in or out. If they
  // have explicitly opted in or out, we want to respect that.
  if (envIsProd) {
    return ff.userSelectedValue;
  }
  return ff.isEnabled;
}

export function useBetaFeatureAccountSelector(): boolean {
  const ff = useBetaFeature('account-selector');
  const isWandbEmployee = useViewerIsWandbEmployee();
  return ff.isEnabled && isWandbEmployee && !envIsLocal;
}

export function useBetaFeatureLaunchPrioritization(): boolean {
  return useBetaFeature('launch-prioritization').isEnabled;
}

export function useBetaFeatureWeavePythonEcosystem(): boolean {
  return useBetaFeature('weave-python-ecosystem').isEnabled;
}

function useBetaFeaturePanelSearchBeta(): boolean {
  return useBetaFeature('panel-search-beta').isEnabled;
}

export function useBetaFeatureNewModelRegistryUI(): boolean {
  return useBetaFeature('model-registry-ui-new').isEnabled;
}

export function usePanelSearchBeta(entityName: string): boolean {
  const isSettingEnabled = useBetaFeaturePanelSearchBeta();
  const isFeatureFlagEnabled = useFeatureFlagPanelSearchBeta(entityName);
  return isSettingEnabled || isFeatureFlagEnabled;
}

export function useViewerIsWandbEmployee() {
  const viewer = useViewer();
  return getViewerIsWandbEmployee(viewer);
}

export function getViewerIsWandbEmployee(viewer: Viewer | undefined): boolean {
  if (viewer == null) {
    return false;
  }
  return (
    envIsPublicCloud &&
    (viewer.admin ||
      viewer.email?.endsWith('@wandb.ai') ||
      viewer.email?.endsWith('@wandb.com'))
  );
}

export function useBetaFeature(featureKey: BetaFeatureKey): BetaFeature {
  const viewerUserInfo = useViewerUserInfoQuery();
  const viewerIsWandbEmployee = useViewerIsWandbEmployee();
  const viewer = useViewer();
  const userInfo = viewerUserInfo?.data?.viewer?.userInfo;
  const userInfoBetaFeatures: UserInfo['betaFeatures'] = userInfo?.betaFeatures;

  const [updateUserInfo] = useUpdateUserInfoMutation();

  const enableForCI =
    envIsIntegration &&
    viewer != null &&
    featureKey !== 'weave-devpopup' &&
    // Please do not change this flag without talking to the Weave team.
    (featureKey !== 'weave1' || !envIsLocal) &&
    featureKey !== 'unicorn-plot' &&
    featureKey !== 'lazy-table' &&
    featureKey !== 'account-selector' &&
    featureKey !== 'named-workspaces';

  let userSelectedValue = userInfoBetaFeatures?.[featureKey];

  // If the user is a wandb employee, then we enable these features by default.
  // However, they should be able to explicitly disable it, so we only enable it if the user hasn't set a value.
  if (
    viewerIsWandbEmployee &&
    userSelectedValue === undefined &&
    (featureKey === 'weave1' || featureKey === 'panel-search-beta')
  ) {
    userSelectedValue = true;
  }

  const isEnabled = (enableForCI || userSelectedValue) ?? false;

  const toggleBetaFeature = useCallback(() => {
    if (userInfo == null) {
      return;
    }
    if (!isEnabled) {
      Analytics.track(`User enabled beta feature ${featureKey}`, {featureKey});
    }

    updateUserInfo({
      variables: {
        userInfo: JSON.stringify({
          ...userInfo,
          betaFeatures: {
            ...(userInfoBetaFeatures ?? {}),
            [featureKey]: !isEnabled,
          },
        }),
      },
    });
  }, [featureKey, isEnabled, updateUserInfo, userInfo, userInfoBetaFeatures]);

  return {
    isEnabled,
    toggleBetaFeature,
    userSelectedValue,
  };
}
